/**
 * Client preferences management utilities
 * These functions help manage user preferences that need to be available during SSR
 */

import { readCardDesignVariantFromCookieString, type CardDesignVariant } from '../analytics/card-design-variant';

export type LayoutPreference = 'list' | 'grid' | 'map';
export type SortPreference = 'relevance' | 'date' | 'salary';

export interface ClientPreferences {
  theme: 'light' | 'dark' | 'system';
  layout: LayoutPreference;
  sort: SortPreference;
  isFinland: boolean;
  // Optional: job state snapshots for SSR parity (IDs only)
  savedJobs?: string[];
  appliedJobs?: string[];
  viewedJobs?: string[];
  // A/B test bucket for the SRP card + job detail page redesign. Deliberately absent from
  // getDefaultClientPreferences() — undefined here signals "not assigned yet" to
  // server/vike-handler.ts, which assigns and persists it on first visit.
  cardDesignVariant?: CardDesignVariant;
}

/**
 * Get client preferences from cookies during SSR
 */
export function getClientPreferencesFromCookies(cookieHeader: string | null): Partial<ClientPreferences> {
  if (!cookieHeader) return {};
  
  const preferences: Partial<ClientPreferences> = {};
  
  // Parse theme cookie
  const themeMatch = cookieHeader.match(/(?:^|;\s*)theme=([^;]+)/);
  if (themeMatch) {
    const theme = decodeURIComponent(themeMatch[1]);
    if (['light', 'dark', 'system'].includes(theme)) {
      preferences.theme = theme as 'light' | 'dark' | 'system';
    }
  }
  
  // Parse layout cookie
  const layoutMatch = cookieHeader.match(/(?:^|;\s*)layout=([^;]+)/);
  if (layoutMatch) {
    const layout = decodeURIComponent(layoutMatch[1]);
    if (['list', 'grid', 'map'].includes(layout)) {
      preferences.layout = layout as LayoutPreference;
    }
  }
  
  // Parse sort cookie
  const sortMatch = cookieHeader.match(/(?:^|;\s*)sort=([^;]+)/);
  if (sortMatch) {
    const sort = decodeURIComponent(sortMatch[1]);
    if (['relevance', 'date', 'salary'].includes(sort)) {
      preferences.sort = sort as SortPreference;
    }
  }
  
  // Parse isFinland cookie
  const isFinlandMatch = cookieHeader.match(/(?:^|;\s*)isFinland=([^;]+)/);
  if (isFinlandMatch) {
    const isFinland = decodeURIComponent(isFinlandMatch[1]);
    preferences.isFinland = isFinland === 'true';
  }

  // Parse cardDesignVariant cookie (A/B test bucket, assigned server-side — see vike-handler.ts)
  const cardDesignVariant = readCardDesignVariantFromCookieString(cookieHeader);
  if (cardDesignVariant) {
    preferences.cardDesignVariant = cardDesignVariant;
  }

  // Parse job state cookies (JSON arrays of IDs)
  const jobKeys = ['savedJobs', 'appliedJobs', 'viewedJobs'] as const;
  for (const key of jobKeys) {
    const m = cookieHeader.match(new RegExp(`(?:^|;\\s*)${key}=([^;]+)`));
    if (m) {
      try {
        const raw = decodeURIComponent(m[1]);
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) (preferences as any)[key] = arr.slice(0, 500).map(String);
      } catch {}
    }
  }
  
  return preferences;
}

/**
 * Get default client preferences
 */
export function getDefaultClientPreferences(): ClientPreferences {
  return {
    theme: 'system',
    layout: 'list',
    sort: 'relevance',
    isFinland: true  // Default to true to match Finland filter slice
  };
}

/**
 * Get effective client preferences (merged with defaults)
 */
export function getEffectiveClientPreferences(cookieHeader: string | null): ClientPreferences {
  const cookiePrefs = getClientPreferencesFromCookies(cookieHeader);
  const defaults = getDefaultClientPreferences();
  
  return {
    ...defaults,
    ...cookiePrefs
  };
}

/**
 * Set a client preference cookie
 */
export function setClientPreferenceCookie(name: string, value: string): void {
  if (typeof document === 'undefined') return;
  
  const oneYearInSeconds = 365 * 24 * 60 * 60;
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${oneYearInSeconds}; SameSite=Lax`;
}

/**
 * Get client preferences from localStorage (client-side only)
 */
export function getClientPreferencesFromLocalStorage(): Partial<ClientPreferences> {
  if (typeof window === 'undefined') return {};
  
  const preferences: Partial<ClientPreferences> = {};
  
  const theme = localStorage.getItem('theme');
  if (theme && ['light', 'dark', 'system'].includes(theme)) {
    preferences.theme = theme as 'light' | 'dark' | 'system';
  }
  
  // Check both 'layout' and 'jobcrawls.viewMode' for backward compatibility
  const layout = localStorage.getItem('layout') || localStorage.getItem('jobcrawls.viewMode');
  if (layout && ['list', 'grid', 'map'].includes(layout)) {
    preferences.layout = layout as LayoutPreference;
  }
  
  const sort = localStorage.getItem('sort');
  if (sort && ['relevance', 'date', 'salary'].includes(sort)) {
    preferences.sort = sort as SortPreference;
  }
  
  const isFinland = localStorage.getItem('isFinland');
  if (isFinland) {
    preferences.isFinland = isFinland === 'true';
  }
  
  // Job state snapshots (IDs only)
  const jobKeys = ['savedJobs', 'appliedJobs', 'viewedJobs'] as const;
  for (const key of jobKeys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) continue;
      const ids = parsed.slice(0, 500).map((x: any) => String(typeof x === 'object' && x?.id ? x.id : x));
      (preferences as any)[key] = ids;
    } catch {}
  }
  
  return preferences;
}

/**
 * Sync localStorage preferences to cookies
 */
export function syncPreferencesToCookies(): void {
  if (typeof window === 'undefined') return;
  
  const prefs = getClientPreferencesFromLocalStorage();
  
  if (prefs.theme) setClientPreferenceCookie('theme', prefs.theme);
  if (prefs.layout) setClientPreferenceCookie('layout', prefs.layout);
  if (prefs.sort) setClientPreferenceCookie('sort', prefs.sort);
  if (prefs.isFinland !== undefined) setClientPreferenceCookie('isFinland', prefs.isFinland.toString());

  // Mirror job state arrays from localStorage if present (IDs only, capped)
  const jobKeys = ['savedJobs', 'appliedJobs', 'viewedJobs'] as const;
  for (const key of jobKeys) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) continue;
      const ids = arr.slice(0, 500).map((x: any) => String(typeof x === 'object' && x?.id ? x.id : x));
      setClientPreferenceCookie(key, JSON.stringify(ids));
    } catch {}
  }
}
